/**
 * Share dialog — create share link, copy URL, social media buttons, QR code.
 * Uses existing gallery share API to generate share tokens.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@frameit/ui';
import { useProjectStore } from '../../stores/projectStore';
import * as api from '../../lib/api-client';
import { copyToClipboard } from '../../lib/clipboard';
import QRCode from 'qrcode';

const isDevMode = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

export interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Share dialog component */
export function ShareDialog({ open, onOpenChange }: ShareDialogProps): React.JSX.Element {
  const project = useProjectStore((s) => s.project);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // QR code state (async generation)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Create share link when dialog opens
  const createShareLink = useCallback(async (): Promise<void> => {
    if (!project || isDevMode) return;
    setIsCreating(true);
    setError(null);
    try {
      const share = await api.createShare(project.id, { label: 'Social share' });
      const url = api.getEmbedUrl(share.token);
      setShareUrl(url);
    } catch {
      setError('Failed to create share link');
    } finally {
      setIsCreating(false);
    }
  }, [project]);

  useEffect(() => {
    if (open && !shareUrl && !isCreating) {
      if (isDevMode) {
        setShareUrl(`${window.location.origin}/embed/demo-token`);
      } else {
        createShareLink();
      }
    }
  }, [open, shareUrl, isCreating, createShareLink]);

  // Generate QR code when shareUrl changes
  useEffect(() => {
    if (!shareUrl) {
      setQrCodeDataUrl(null);
      return;
    }

    let cancelled = false;
    setQrLoading(true);

    QRCode.toDataURL(shareUrl, {
      width: 160,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((dataUrl) => {
        if (!cancelled) {
          setQrCodeDataUrl(dataUrl);
          setQrLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQrCodeDataUrl(null);
          setQrLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shareUrl]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setShareUrl(null);
      setCopied(false);
      setError(null);
      setQrCodeDataUrl(null);
    }
  }, [open]);

  const handleCopy = async (): Promise<void> => {
    if (!shareUrl) return;
    await copyToClipboard(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Social share URLs
  const socialUrls = shareUrl
    ? (() => {
        const text = `Check out my framed artwork on FrameIt!`;
        const encoded = encodeURIComponent(shareUrl);
        const encodedText = encodeURIComponent(text);
        return {
          twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encoded}`,
          facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
          pinterest: `https://pinterest.com/pin/create/button/?url=${encoded}&description=${encodedText}`,
          email: `mailto:?subject=${encodedText}&body=${encodedText}%0A%0A${encoded}`,
        };
      })()
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Share Artwork</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Loading state */}
          {isCreating && (
            <div className="text-center py-4">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Creating share link...</p>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="text-center py-4">
              <p className="text-sm text-destructive mb-2">{error}</p>
              <Button size="sm" variant="outline" onClick={createShareLink}>
                Try Again
              </Button>
            </div>
          )}

          {/* Share content */}
          {shareUrl && (
            <>
              {/* Copy link */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Share Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 h-9 px-3 text-xs rounded-md border bg-muted/50 font-mono"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <Button size="sm" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Social buttons */}
              {socialUrls && (
                <div>
                  <label className="text-xs text-muted-foreground block mb-1.5">Share on</label>
                  <div className="flex gap-2">
                    {/* Twitter/X */}
                    <a
                      href={socialUrls.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 h-9 flex items-center justify-center gap-1.5 rounded-md border text-xs hover:bg-accent transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      X
                    </a>

                    {/* Facebook */}
                    <a
                      href={socialUrls.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 h-9 flex items-center justify-center gap-1.5 rounded-md border text-xs hover:bg-accent transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      FB
                    </a>

                    {/* Pinterest */}
                    <a
                      href={socialUrls.pinterest}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 h-9 flex items-center justify-center gap-1.5 rounded-md border text-xs hover:bg-accent transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z" />
                      </svg>
                      Pin
                    </a>

                    {/* Email */}
                    <a
                      href={socialUrls.email}
                      className="flex-1 h-9 flex items-center justify-center gap-1.5 rounded-md border text-xs hover:bg-accent transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      Email
                    </a>
                  </div>
                </div>
              )}

              {/* QR Code */}
              <div className="text-center">
                <label className="text-xs text-muted-foreground block mb-1.5">QR Code</label>
                {qrLoading && (
                  <div className="inline-block p-3">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                )}
                {qrCodeDataUrl && (
                  <div className="inline-block p-3 bg-white rounded-lg border">
                    <img src={qrCodeDataUrl} alt="QR Code" width={160} height={160} />
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">Scan to view on mobile</p>
              </div>

              {/* Embed code */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Embed Code</label>
                <textarea
                  readOnly
                  rows={2}
                  value={`<iframe src="${shareUrl}" width="600" height="500" frameborder="0" style="border:none;border-radius:8px;"></iframe>`}
                  className="w-full px-3 py-2 text-xs rounded-md border bg-muted/50 font-mono resize-none"
                  onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
